import React from "react";
import {DescriptionOutlined} from "@mui/icons-material";
import Applet from "../../Applet";
import ObjectRevisionTable from "../../../../components/Foundation/ObjectRevisionTable";

// The record grid for any first-class object, over the ObjectRevision EC scoped by the page to one
// ObjectClassification. The title comes from the page; the icon and the EC filter are the only other
// things that differ between classifications.
class ObjectRevisionTableApplet extends Applet {

    // THE TICK COLUMN IS DRAWN ONLY WHERE SOMETHING ACTS ON IT. Placement is what renders (see the
    // note inside Placement below), so declaring the column and not placing it would draw nothing,
    // and placing it unconditionally would put a tick box in front of every reader of this grid --
    // including the ones with no toolbar to use it with.
    placement() {
        if (!this.props.selectable) return this.state.Placement;
        const out = {};
        for (const breakpoint of Object.keys(this.state.Placement)) {
            out[breakpoint] = [{id: "AskSelection"}, ...this.state.Placement[breakpoint]];
        }
        return out;
    }

    // A PROJECT'S CARD. Ed, 2026-10-29: "each project card is showing Type and Revision. They should
    // be removed. Let's also add indications for the number of Products, Quote Requests, Quote
    // Response, Contracts and Build Plans." A project has one type by design and no revision chain,
    // so those two fields said "PRJ-" and "1" on every card; what it HOLDS is the fact a reader
    // scans for, and it takes the corner the artefact pills have on every other list.
    card() {
        if (this.props.classification !== "Project") return this.state.Card;
        return {
            ...this.state.Card,
            corner: "Project Contents Cell",
            fields: this.state.Card.fields.filter(one => one.label !== "Type" && one.label !== "Revision"),
        };
    }

    render() {
        return <ObjectRevisionTable applet={this}
                                    card={this.card()}
                                    classification={this.props.classification}
                                    actions={this.props.actions}
                                    onSelectAllShown={this.props.onSelectAllShown}
                                    onClearSelection={this.props.onClearSelection}
                                    pageSize={this.props.PageSize ?? this.state.PageSize}
                                    placement={this.placement()}
                                    columns={this.state.Columns}
                                    selectedUSIDs={this.props.selectedUSIDs}
                                    onToggleSelect={this.props.onToggleSelect}
                                    pickOnClick={this.props.pickOnClick}
                                    title={this.props.title ?? "Records"}
                                    icon={<DescriptionOutlined/>}
                                    enterpriseComponent={this.getEnterpriseComponent()}
                                    sort={this.state.Sort}
                                    menu={this.state.Menu}
                                    rowHeight={this.state.RowHeight}
                                    simpleSearch={this.state.SimpleSearch}
                                    enableSearch={true}
                                    enableSort={true}
        />;
    }

    state = {
        Name: "ObjectRevisionTableApplet",
        Menu: {
            options: [
                {displayValue: "Refresh", value: "Refresh"},
            ],
        },
        // 48px rows, matching the suite-wide grid sizing.
        RowHeight: 48,

        /**
         * THE CARD VIEW, drawn by every species list because they all share this applet.
         *
         * IT IS NOW THE DEFAULT, WHICH REVERSES WHAT THIS COMMENT USED TO SAY. The argument here
         * was that a record list is SCANNED — run down the ObjectID column until you find it — and
         * that ten cards a page turns a scan into four times the page-turns. That argument is
         * sound and it is not the common case: a reader who already knows the ObjectID uses the
         * filters or the sort, and a reader who does not is deciding WHICH record to open, which
         * is a decision made on type, owner, approval state and effective date. Those are fields,
         * not a column, so they are a card. The grid is one click away and keeps the scan.
         *
         * Ed, 2026-10-26, looking at Suppliers: make the card view the default presentation of
         * each object landing page.
         *
         * WHAT IT SHOWS IS WHAT THE DETAIL PANE LEADS WITH, which is the point of the view: the
         * eight fields a reader would otherwise open the record for. Several are attributes no
         * column places — Revision and Approval are hidden by default in the grid, and the card
         * reads the record in the buffer rather than the row, so it can show them anyway.
         *
         * THE DATES USE `whenSet` NAMING THEIR OWN ATTRIBUTE. A draft has no released, effective
         * or approved date, and "Released —" three times over on every draft is three labels
         * telling the reader nothing the Status pill has not already said.
         */
        Card: {
            // CARDS ARE THE DEFAULT PRESENTATION OF EVERY OBJECT LANDING PAGE. One applet drives
            // all of them -- Documents, Suppliers, Purchase Orders, every classification ObjectList
            // shows -- so this one line decides what a reader arrives at across the product, and
            // the grid is one click away on the view toggle.
            //
            // IT IS THE RIGHT DEFAULT BECAUSE OF WHAT THESE LISTS ARE FOR. A row gives a reader the
            // six or seven columns that fit the width; the card gives them the fields the record's
            // own detail page leads with, plus what it HAS -- files, controlled documents, an SBOM
            // -- in the corner. Somebody opening Suppliers is deciding which supplier to open, and
            // that decision is made on type, owner, approval state and effective date, which is a
            // card. Scanning one column down a hundred rows is the grid's job, and it keeps it.
            //
            // THE TOGGLE LASTS ONE VISIT, WHICH IS WHY THIS MATTERS MORE THAN IT LOOKS.
            // `MemoryContext.components[...]` is assigned a fresh object in PlatformTable's
            // constructor, so a reader who switches to the grid is back on cards the next time they
            // arrive. This default is therefore what nearly every reader sees nearly every time.
            defaultOn: true,
            pageSize: 10,
            headerName: "",
            title: "ObjectID",
            // DRAWN BY THE GRID'S OWN IDENTIFIER CELL: the number opens the record drawer, the icon
            // beside it a new window (Ed, 2026-10-30). The card and the column cannot disagree.
            titleRenderer: "Object Id Link Cell",
            subtitle: "Name",
            // THE GRID'S OWN STATUS CELL, not a bare pill. This column draws "RELEASED · in force
            // 15 Jan" for a revision that is released but not yet governing, and that marker is
            // decided by IsCurrent and EffectiveDatetime rather than by the status -- so a plain
            // pill cannot carry it. Opening these lists as cards with `chip: "Status"` dropped it
            // silently: two revisions reading RELEASED and nothing saying which one is in force,
            // which is the exact duplicate Ed reported on TC-00002 and this column was built to
            // answer. Caught by the training suite, which is why that test exists.
            chipRenderer: "Status Pill Cell",
            // THE "Has" COLUMN, IN THE CORNER. Which records carry files, controlled documents or
            // an SBOM is the question a reader scans this list for, and it is the one thing on the
            // row that is not a field of the record -- it is COMPUTED per page from the same query
            // the Attachments panel answers from. Naming the grid's own renderer rather than
            // rebuilding it is what stops the card and the row disagreeing.
            corner: "Record Artefacts Cell",
            fields: [
                // ObjectTypeAcronym, NOT ObjectTypeName -- which is not an attribute this
                // component declares, so it read undefined and drew "—" on every card for every
                // reader. A card field that names a non-existent attribute fails exactly this
                // silently: no error, no warning, an em dash that looks like missing data rather
                // than a missing field. The structural test beside this applet is what stops the
                // next one.
                //
                // AND THE ACRONYM IS THE USEFUL ANSWER HERE. The detail pane's "Type" is the
                // CLASSIFICATION -- "Document" -- which every row in a Documents list shares and
                // which therefore tells a reader nothing; SOP against WI is the distinction they
                // are actually scanning for.
                {label: "Type", field: "ObjectTypeAcronym"},
                {label: "Revision", field: "DisplayRevision"},
                {label: "Owner", field: "PrimaryPositionName"},
                {label: "Approval", field: "ApprovalStatus", whenSet: "ApprovalStatus"},
                {label: "Created", field: "CreatedDatetime"},
                {label: "Released", field: "ReleasedDatetime", whenSet: "ReleasedDatetime"},
                {label: "Effective", field: "EffectiveDatetime", whenSet: "EffectiveDatetime"},
                {label: "Approved", field: "ApprovedDatetime", whenSet: "ApprovedDatetime"},
                {label: "Description", field: "Description", whenSet: "Description"},
            ],
        },
        // 40 rows by default. xs/sm stay small because a phone cannot render 40 rows usefully.
        PageSize: {"default": 40, "xs": 3, "sm": 3, "md": 40, "lg": 40, "lx": 40},
        Placement: {
            "xl": [
                {id: "ObjectID"},
                {id: "Name"},
                {id: "PrimaryPositionName"},
                {id: "Status"},
                // PLACEMENT IS WHAT RENDERS, not Columns. A column defined in Columns and absent
                // here is simply not drawn -- no error, no empty column, nothing. Adding it to
                // one and not the other is the oldest mistake available in this file.
                {id: "Artefacts"},
                {id: "CreatedDatetime"},
                {id: "DisplayRevision"},
                {id: "Description"},
                {id: "EffectiveDatetime"},
                {id: "ApprovedDatetime"},
                {id: "LastModifiedDatetime"},
            ],
        },
        Columns: [
            // CHROME, NOT DATA. Narrow, unheaded and not hideable, the same shape as the row-selection
            // indicator the other grids use. The row has no "AskSelection" attribute and does not
            // need one: the renderer reads the row's USID and the page's selection, so the column's
            // own value is never looked at.
            // NOW HEADED, with a select-all checkbox. It was header:null -- an unheaded column of
            // checkboxes beside a button reading "tick up to 25 records", which Ed read as a
            // puzzle: "where do I do this?" Nothing on screen connected the two.
            {id: "AskSelection", width: 44, cellRenderer: "Ask Selection Cell", header: "enabled", headerRenderer: "Ask Selection Header", headerTitle: "", hideable: false},
            // Required columns (hideable:false) are always shown and locked on in the Columns menu.
            // Optional columns default to hidden (show:false) and are available from that menu.
            {id: "ObjectID", width: 150, cellRenderer: "Object Number Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Object ID", hideable: false},
            // FLEXED, SO THE GRID HAS NO LEFTOVER. The fixed widths added up to less than the
            // grid, so MuiDataGrid padded the difference with an empty filler cell -- invisible
            // while the current row was painted grey and conspicuous the moment it took a blue
            // outline instead, which traced a box around nothing. Ed photographed it.
            //
            // THE SLACK GOES TO Name BECAUSE Name IS WHAT RAN OUT. Document titles were being cut
            // off ("Operator Training Standard Operating Procedu...") with 190px sitting empty two
            // columns to the right. Giving the remainder to the one column that needed it removes
            // the filler and the ellipsis together. minWidth keeps its old 320 as a floor.
            {id: "Name", flex: 1, minWidth: 320, cellRenderer: "Standard Single Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Name", hideable: false},
            // OWNER IS THE PRIMARY POSITION, headed "Owner" rather than "PrimaryPositionName" because that is
            // the question a reader is asking of a list. The attribute keeps its own name: it is
            // POSITION.NAME joined on PrimaryPositionUSID, and renaming it would mean touching the
            // EC, the IDOC and the payload for a column heading.
            {id: "PrimaryPositionName", width: 320, cellRenderer: "Owner Position Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Owner"},
            {id: "Status", width: 140, cellRenderer: "Status Pill Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Status", hideable: false},
            // AFTER STATUS, because it answers the next question somebody scanning this
            // grid has: which of these carry a bill of materials, a document, or files.
            //
            // 180px because the cell now holds up to THREE chips -- "4 files", "Docs" and
            // "SBOM" -- where it once held two; at 150 the third wrapped.
            //
            // PRESENCE FOR SBOM AND DOCS, A COUNT FOR FILES, and the difference is where the
            // number comes from. A count MAINTAINED on every write by whatever creates the thing
            // counted goes stale, and a stale count on a quality record is a lie. This one is
            // COMPUTED, once per page of rows, by the same query the Attachments panel answers
            // from -- so it cannot disagree with the panel, and there is nothing to drift.
            //
            // Empty on most rows by design; an SOP will never carry an SBOM.
            {id: "Artefacts", width: 180, cellRenderer: "Record Artefacts Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Has"},
            // STILL SORTED ON, no longer SHOWN. It remains the default sort -- newest first is what a
            // list of records should open as -- and is available from the Columns menu, but a date
            // nobody was reading was taking 230px from the name and the owner.
            //
            // Its own renderer, because the built-in "Local Datetime Cell" formats with formatLocal,
            // not formatLocal2.
            {id: "CreatedDatetime", width: 230, cellRenderer: "Local Datetime 2 Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Created", show: false},
            // Available from the Columns menu, not presented by default.
            {id: "DisplayRevision", width: 110, cellRenderer: "Standard Single Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Revision", show: false},
            {id: "Description", width: 320, cellRenderer: "Standard Single Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Description", show: false},
            // "Local Datetime Cell", NOT "Last Modified Datetime Cell" -- the latter renders a
            // relative age rather than a date.
            {id: "EffectiveDatetime", width: 190, cellRenderer: "Local Datetime Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Effective", show: false},
            {id: "ApprovedDatetime", width: 170, cellRenderer: "Local Datetime Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Approved", show: false},
            {id: "LastModifiedDatetime", width: 180, cellRenderer: "Local Datetime Cell", header: "enabled", headerRenderer: "Standard Single Header", headerTitle: "Last Modified", show: false},
        ],
        // A sort SPECIFICATION, not a column list. PlatformTable reads activeSort.Options and
        // iterates it, so an entry without an Options array crashes the header renderer on first
        // paint -- which is exactly what a {id, direction} shape did.
        Sort: [
            // NEWEST FIRST, and it is the default for a reason that got sharper once the list
            // stopped hiding revisions: with every revision shown, a draft you have just raised is
            // the newest row in the table, so it lands at the top of the first page. Ordering by
            // Object ID instead buried it wherever its number happened to fall.
            {
                "Identifier": "Created",
                "Visible": true,
                "Default": true,
                "Options": [{
                    "Direction": "DESC",
                    "SortAttribute": "CreatedDatetime",
                    "DisplayName": "Created",
                }],
            },
            // DESCENDING, matching Created above rather than reading forwards. Numbers are issued in
            // order, so the highest Object ID is the newest record -- both sorts therefore put the
            // newest work at the top, and switching between them does not turn the list upside down.
            {
                "Identifier": "Object ID",
                "Visible": true,
                "Default": false,
                "Options": [{
                    "Direction": "DESC",
                    "SortAttribute": "ObjectID",
                    "DisplayName": "Object ID",
                }],
            },
            {
                "Identifier": "Name",
                "Visible": true,
                "Default": false,
                "Options": [{
                    "Direction": "ASC",
                    "SortAttribute": "Name",
                    "DisplayName": "Name",
                }],
            },
            {
                "Identifier": "Last Modified",
                "Visible": true,
                "Default": false,
                "Options": [{
                    "Direction": "DESC",
                    "SortAttribute": "LastModifiedDatetime",
                    "DisplayName": "Last Modified",
                }],
            },
        ],
        // Search over the fields a user actually looks a document up by. The framework wants an
        // Actions.Query.Groups membership list AND a Controls entry per field -- a control without a
        // membership entry never renders, and a membership entry without a control throws.
        //
        // "No Special Characters" is deliberately NOT applied to these: document numbers contain
        // hyphens (SOP-00001), so that validator would reject the most obvious search term of all.
        SimpleSearch: {
            Actions: {
                Query: {
                    Groups: [
                        {
                            "identifier": "group1",
                            "type": "standard",
                            "selector": null,
                            "characteristics": [],
                            // NO "SearchableContent" MEMBER. The full-text field lives in the page
                            // header now (SearchRecords, driven through PlatformTable's
                            // onFullTextSearchHandler), and the same field in two places is two
                            // states that can disagree about what the reader asked for.
                            // Ed, 2026-10-26: "remove the Search text field from within the
                            // PlatformTable search so it does not appear twice."
                            "membership": [
                                {
                                    id: "ObjectID",
                                },
                                {
                                    id: "Name",
                                },
                                {
                                    id: "Description",
                                },
                                {
                                    id: "Status",
                                },
                            ]
                        }
                    ]
                }
            },
            Controls: {
                "ObjectID": {
                    id: "ObjectID",
                    enterpriseComponentAttribute: "ObjectID",
                    placement: {
                        "Query": {
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                            xl: 3
                        }
                    },
                    errors: [
                        {
                            validator: "No Leading Spaces",
                            value: null,
                            errorText: "Field must not have leading spaces.",
                        },
                        {
                            validator: "Not All Spaces",
                            value: null,
                            errorText: "Field must not have all spaces.",
                        },
                    ],
                    csp: {
                        autoFocus: false,
                        multiline: false,
                        placeholder: null,
                        label: "Number",
                        disabled: false,
                        size: "small"
                    },
                    ip: {},
                    type: "TextField",
                    requiresConfirmation: false,
                },
                "Name": {
                    id: "Name",
                    enterpriseComponentAttribute: "Name",
                    placement: {
                        "Query": {
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                            xl: 3
                        }
                    },
                    errors: [
                        {
                            validator: "No Leading Spaces",
                            value: null,
                            errorText: "Field must not have leading spaces.",
                        },
                        {
                            validator: "Not All Spaces",
                            value: null,
                            errorText: "Field must not have all spaces.",
                        },
                    ],
                    csp: {
                        autoFocus: false,
                        multiline: false,
                        placeholder: null,
                        label: "Name",
                        disabled: false,
                        size: "small"
                    },
                    ip: {},
                    type: "TextField",
                    requiresConfirmation: false,
                },
                "Description": {
                    id: "Description",
                    enterpriseComponentAttribute: "Description",
                    placement: {
                        "Query": {
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                            xl: 3
                        }
                    },
                    errors: [
                        {
                            validator: "No Leading Spaces",
                            value: null,
                            errorText: "Field must not have leading spaces.",
                        },
                        {
                            validator: "Not All Spaces",
                            value: null,
                            errorText: "Field must not have all spaces.",
                        },
                    ],
                    csp: {
                        autoFocus: false,
                        multiline: false,
                        placeholder: null,
                        label: "Description",
                        disabled: false,
                        size: "small"
                    },
                    ip: {},
                    type: "TextField",
                    requiresConfirmation: false,
                },
                "Status": {
                    id: "Status",
                    enterpriseComponentAttribute: "Status",
                    placement: {
                        "Query": {
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                            xl: 3
                        }
                    },
                    errors: [
                        {
                            validator: "No Leading Spaces",
                            value: null,
                            errorText: "Field must not have leading spaces.",
                        },
                        {
                            validator: "Not All Spaces",
                            value: null,
                            errorText: "Field must not have all spaces.",
                        },
                    ],
                    csp: {
                        autoFocus: false,
                        multiline: false,
                        placeholder: null,
                        label: "Status",
                        disabled: false,
                        size: "small"
                    },
                    ip: {},
                    type: "TextField",
                    requiresConfirmation: false,
                },
            },
        },
    };
}

export default ObjectRevisionTableApplet;
